import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { workApiFetch } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import {
  IMAGE_UPLOAD_TYPES,
  putStoredFileBytes,
  StoredFileUploadError,
  sha256Base64,
} from "../lib/storedFileUpload";
import type { WorkRequestId } from "../lib/workIds";

export { ATTACHMENT_UPLOAD_TIMEOUT_MS } from "../lib/storedFileUpload";

export const WORK_REQUEST_ATTACHMENT_TYPES = IMAGE_UPLOAD_TYPES;

/** Matches the API's Work Request attachment limits (same as Work Orders). */
export const WORK_REQUEST_ATTACHMENT_MAX_BYTES = 25 * 1024 * 1024;
export const WORK_REQUEST_ATTACHMENT_MAX_COUNT = 20;

/**
 * Attachment download links are presigned for five minutes, so cached links
 * are refreshed before they expire.
 */
export const workRequestFilesStaleTime = 4 * 60 * 1000;

const uploadSessionResponseSchema = z.object({
  uploadId: z.string().uuid(),
  uploadUrl: z.string().url().nullable(),
  isUploaded: z.boolean(),
  expiresIn: z.number(),
});
const completeUploadResponseSchema = z.object({
  uploadId: z.string().uuid(),
  uploadStatus: z.string(),
  scanStatus: z.string(),
});
const fileResponseSchema = z.object({ fileId: z.string().uuid() });
const workRequestFileSchema = z.object({
  id: z.string().uuid(),
  fileName: z.string(),
  fileSize: z.number().int().nonnegative(),
  author: z.string(),
  createdOn: z.string(),
  canRemove: z.boolean(),
  downloadUrl: z.string().url(),
});
const workRequestFilesResponseSchema = z.object({
  files: z.array(workRequestFileSchema),
});

export type WorkRequestFile = z.infer<typeof workRequestFileSchema>;

/**
 * A Work Request attachment step that failed. `status` is the HTTP status when
 * the API rejected the call, or null when the client stopped it first.
 */
export class WorkRequestFilesRequestError extends Error {
  constructor(
    message: string,
    readonly status: number | null = null,
  ) {
    super(message);
    this.name = "WorkRequestFilesRequestError";
  }
}

export type WorkRequestFilesScope = {
  customerId: string;
  facilityId: string;
  requestId: WorkRequestId;
};

export type WorkRequestFileUploadInput = {
  uploadId: string;
  fileName: string;
  contentType: string;
  contentLength: number;
  checksumSha256: string;
};

function requestPath({
  customerId,
  facilityId,
  requestId,
}: WorkRequestFilesScope) {
  return `/v1/customers/${encodeURIComponent(customerId)}/facilities/${encodeURIComponent(facilityId)}/requests/${encodeURIComponent(requestId)}`;
}

async function errorMessage(response: Response) {
  const body: unknown = await response.json().catch(() => null);
  if (
    typeof body === "object" &&
    body !== null &&
    "error" in body &&
    typeof body.error === "string"
  )
    return body.error;
  return `Work Request attachment request failed (${response.status}).`;
}

async function requestJson<T>(
  path: string,
  schema: z.ZodType<T>,
  init?: RequestInit,
) {
  const response = await workApiFetch(path, init);
  if (!response.ok)
    throw new WorkRequestFilesRequestError(
      await errorMessage(response),
      response.status,
    );
  return schema.parse(await response.json());
}

function postJson(body: unknown): RequestInit {
  return {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  };
}

export function createWorkRequestFileUpload(
  scope: WorkRequestFilesScope,
  input: WorkRequestFileUploadInput,
) {
  return requestJson(
    `${requestPath(scope)}/upload-url`,
    uploadSessionResponseSchema,
    postJson(input),
  );
}

export function retryWorkRequestFileUpload(
  scope: WorkRequestFilesScope,
  uploadId: string,
) {
  return requestJson(
    `${requestPath(scope)}/upload-url/retry`,
    uploadSessionResponseSchema,
    postJson({ uploadId }),
  );
}

export function completeWorkRequestFileUpload(
  scope: WorkRequestFilesScope,
  uploadId: string,
) {
  return requestJson(
    `${requestPath(scope)}/upload-url/completion`,
    completeUploadResponseSchema,
    postJson({ uploadId }),
  );
}

export async function listWorkRequestFiles(scope: WorkRequestFilesScope) {
  const response = await requestJson(
    `${requestPath(scope)}/files`,
    workRequestFilesResponseSchema,
  );
  return response.files;
}

export function workRequestFilesQueryKey(
  requestId: WorkRequestId,
  customerId?: string,
  facilityId?: string,
) {
  return ["work-requests", requestId, "files", customerId, facilityId] as const;
}

export function useWorkRequestFilesQuery(requestId: WorkRequestId) {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const customerId = selectedCustomer?.id;
  const facilityId = selectedFacility?.id;

  return useQuery({
    queryKey: workRequestFilesQueryKey(requestId, customerId, facilityId),
    queryFn: () => {
      if (!customerId || !facilityId)
        throw new Error("A customer and facility must be selected.");
      return listWorkRequestFiles({ customerId, facilityId, requestId });
    },
    enabled: Boolean(customerId && facilityId),
    staleTime: workRequestFilesStaleTime,
    refetchInterval: workRequestFilesStaleTime,
  });
}

export function attachWorkRequestFile(
  scope: WorkRequestFilesScope,
  uploadId: string,
) {
  return requestJson(
    `${requestPath(scope)}/files`,
    fileResponseSchema,
    postJson({ uploadId }),
  );
}

export function removeWorkRequestFile(
  scope: WorkRequestFilesScope,
  fileId: string,
) {
  return requestJson(
    `${requestPath(scope)}/files/${encodeURIComponent(fileId)}`,
    fileResponseSchema,
    { method: "DELETE" },
  );
}

/** Uploads the bytes to the presigned URL; fails rather than hang on S3. */
export async function putWorkRequestFileBytes(
  uploadUrl: string,
  file: File,
  checksumSha256: string,
) {
  try {
    await putStoredFileBytes(uploadUrl, file, checksumSha256, file.type);
  } catch (error) {
    if (error instanceof StoredFileUploadError)
      throw new WorkRequestFilesRequestError(error.message, error.status);
    throw error;
  }
}

function isSupportedType(
  type: string,
): type is (typeof WORK_REQUEST_ATTACHMENT_TYPES)[number] {
  return (WORK_REQUEST_ATTACHMENT_TYPES as readonly string[]).includes(type);
}

/**
 * Stages, uploads and verifies one file for a Work Request. Returns the
 * verified upload id; nothing is attached until `attachWorkRequestFile`.
 */
export async function prepareWorkRequestFile(
  scope: WorkRequestFilesScope,
  file: File,
) {
  if (!isSupportedType(file.type))
    throw new WorkRequestFilesRequestError(
      `${file.name} is not a supported image type.`,
    );
  const checksumSha256 = await sha256Base64(file);
  const uploadId = crypto.randomUUID();
  const session = await createWorkRequestFileUpload(scope, {
    uploadId,
    fileName: file.name,
    contentType: file.type,
    contentLength: file.size,
    checksumSha256,
  });
  if (!session.isUploaded) {
    if (!session.uploadUrl)
      throw new WorkRequestFilesRequestError(
        "The upload URL is unavailable; retry the upload.",
      );
    await putWorkRequestFileBytes(session.uploadUrl, file, checksumSha256);
  }
  await completeWorkRequestFileUpload(scope, uploadId);
  return uploadId;
}

export function useWorkRequestFiles() {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();
  function scope(requestId: WorkRequestId): WorkRequestFilesScope {
    if (!selectedCustomer || !selectedFacility)
      throw new Error("A customer and facility must be selected.");
    return {
      customerId: selectedCustomer.id,
      facilityId: selectedFacility.id,
      requestId,
    };
  }

  // Attaching changes the request, so it is the mutation that owns
  // invalidation. The sheet reports failures inline per file, so there is no
  // toast here.
  const attachFile = useMutation({
    mutationFn: (input: { requestId: WorkRequestId; uploadId: string }) =>
      attachWorkRequestFile(scope(input.requestId), input.uploadId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["work-requests"] }),
  });

  // Upload staging does not change cached data, so it stays a plain call.
  return {
    attachFile,
    prepareUpload: (input: { requestId: WorkRequestId; file: File }) =>
      prepareWorkRequestFile(scope(input.requestId), input.file),
  };
}
